import React from "react";
import { Expand, Minus, Plus, RotateCcw } from "lucide-react";

import SeatLayoutCanvas, { SeatLayoutLegend } from "./SeatLayoutCanvas";


const MIN_ZOOM_LEVEL = 0.75;
const MAX_ZOOM_LEVEL = 2.5;
const ZOOM_STEP = 0.25;

const clampZoomLevel = (zoomLevel) => Math.max(MIN_ZOOM_LEVEL, Math.min(MAX_ZOOM_LEVEL, zoomLevel));

function SeatLayoutZoomViewer({
  layout,
  selectedSeatIds = [],
  onSelectSeat,
  viewportClassName = "max-h-[560px]",
  onExpand,
  showLegend = true,
  readOnly = false,
  autoFit = false,
}) {
  const [zoomLevel, setZoomLevel] = React.useState(1);
  const viewportRef = React.useRef(null);

  React.useEffect(() => {
    setZoomLevel(1);
    if (viewportRef.current) {
      viewportRef.current.scrollTop = 0;
      viewportRef.current.scrollLeft = 0;
    }
  }, [layout]);

  const updateZoom = (direction) => {
    setZoomLevel((currentZoom) => clampZoomLevel(currentZoom + direction * ZOOM_STEP));
  };

  const resetView = () => {
    setZoomLevel(1);
    if (viewportRef.current) {
      viewportRef.current.scrollTop = 0;
      viewportRef.current.scrollLeft = 0;
    }
  };

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-3.5 py-2.5">
          <div className="inline-flex items-center gap-1.5">
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#e7e2d8] text-[#6b6456] transition hover:border-[#d9d3c6] hover:bg-[#f7f4ee] disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => updateZoom(-1)}
              disabled={zoomLevel <= MIN_ZOOM_LEVEL}
              aria-label="배치도 축소"
              title="축소"
            >
              <Minus size={16} />
            </button>
            <span className="min-w-[46px] text-center text-sm font-bold tabular-nums text-[#5f5949]">
              {Math.round(zoomLevel * 100)}%
            </span>
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#e7e2d8] text-[#6b6456] transition hover:border-[#d9d3c6] hover:bg-[#f7f4ee] disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => updateZoom(1)}
              disabled={zoomLevel >= MAX_ZOOM_LEVEL}
              aria-label="배치도 확대"
              title="확대"
            >
              <Plus size={16} />
            </button>
            <span className="mx-1 h-5 w-px bg-[#e7e2d8]" />
            <button
              type="button"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#e7e2d8] text-[#6b6456] transition hover:border-[#d9d3c6] hover:bg-[#f7f4ee]"
              onClick={resetView}
              aria-label="원래 크기"
              title="원래 크기"
            >
              <RotateCcw size={16} />
            </button>
          </div>

          {onExpand ? (
            <button
              type="button"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#efe9dd] text-[#5f5949] transition hover:bg-[#e6ded0] active:scale-[0.97]"
              onClick={onExpand}
              aria-label="크게 보기"
              title="크게 보기"
            >
              <Expand size={16} />
            </button>
          ) : null}
        </div>

        <div
          ref={viewportRef}
          className={`overflow-auto ${viewportClassName}`.trim()}
        >
          <div
            className="mx-auto min-w-full"
            style={{ width: `${zoomLevel * 100}%` }}
          >
            <SeatLayoutCanvas
              layout={layout}
              selectedSeatIds={selectedSeatIds}
              onSelectSeat={onSelectSeat}
              readOnly={readOnly}
              showLegend={false}
              autoFit={autoFit}
              bare
            />
          </div>
        </div>
      </div>

      {showLegend ? <SeatLayoutLegend /> : null}
    </div>
  );
}

export default SeatLayoutZoomViewer;